import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiFile,
  FiSave,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import CustomerSearchSelect from "./CustomerSearchSelect";
import AddCustomerDialog from "./AddCustomerDialog";

import {
  INITIAL_REQUIREMENT_FORM,
  REQUIREMENT_PRIORITIES,
  REQUIREMENT_STATUSES,
  REQUIREMENT_TYPES,
  getAttachmentFileName,
} from "./constants";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

const AddEditRequirement = ({
  open,
  requirement,
  onClose,
  onSuccess,
}) => {
  const api = useMemo(() => new API(), []);
  const swal = useSwal();

  const isEdit = Boolean(requirement?._id);

  const [form, setForm] = useState({
    ...INITIAL_REQUIREMENT_FORM,
  });

  const [
    selectedCustomer,
    setSelectedCustomer,
  ] = useState(null);

  const [
    existingAttachments,
    setExistingAttachments,
  ] = useState([]);

  const [
    newAttachments,
    setNewAttachments,
  ] = useState([]);

  const [
    addCustomerOpen,
    setAddCustomerOpen,
  ] = useState(false);

  const [
    addCustomerInitialValues,
    setAddCustomerInitialValues,
  ] = useState({
    customerName: "",
    mobile: "",
  });

  const [errors, setErrors] =
    useState({});

  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (requirement) {
      const customerObject =
        requirement?.customer &&
        typeof requirement.customer ===
          "object"
          ? requirement.customer
          : null;

      setForm({
        customer:
          customerObject?._id ||
          requirement?.customer ||
          "",

        requirementType:
          requirement?.requirementType ||
          "",

        description:
          requirement?.description || "",

        priority:
          requirement?.priority ||
          "Medium",

        status:
          requirement?.status ||
          "Active",
      });

      setSelectedCustomer(
        customerObject
      );

      setExistingAttachments(
        Array.isArray(
          requirement?.attachments
        )
          ? requirement.attachments
          : []
      );
    } else {
      setForm({
        ...INITIAL_REQUIREMENT_FORM,
      });

      setSelectedCustomer(null);
      setExistingAttachments([]);
    }

    setNewAttachments([]);
    setErrors({});
    setSubmitting(false);
  }, [open, requirement]);

  if (!open) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } =
      event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));
  };

  const handleCustomerChange = (
    customer
  ) => {
    setSelectedCustomer(customer);

    setForm((previous) => ({
      ...previous,
      customer:
        customer?._id || "",
    }));

    setErrors((previous) => ({
      ...previous,
      customer: "",
    }));
  };

  const handleOpenAddCustomer = (
    initialValues
  ) => {
    setAddCustomerInitialValues({
      customerName:
        initialValues?.customerName ||
        "",
      mobile:
        initialValues?.mobile || "",
    });

    setAddCustomerOpen(true);
  };

  const handleCustomerCreated = (
    customer
  ) => {
    setSelectedCustomer(customer);

    setForm((previous) => ({
      ...previous,
      customer:
        customer?._id || "",
    }));

    setErrors((previous) => ({
      ...previous,
      customer: "",
    }));

    setAddCustomerOpen(false);
  };

  const handleFiles = (event) => {
    const selectedFiles =
      Array.from(
        event.target.files || []
      );

    const currentCount =
      existingAttachments.length +
      newAttachments.length;

    const availableSlots = Math.max(
      MAX_ATTACHMENTS - currentCount,
      0
    );

    const validFiles = [];
    const invalidMessages = [];

    selectedFiles.forEach((file) => {
      if (
        !ALLOWED_FILE_TYPES.includes(
          file.type
        )
      ) {
        invalidMessages.push(
          `${file.name}: unsupported file type`
        );

        return;
      }

      if (
        file.size > MAX_FILE_SIZE
      ) {
        invalidMessages.push(
          `${file.name}: file size exceeds 5 MB`
        );

        return;
      }

      validFiles.push(file);
    });

    const filesToAdd =
      validFiles.slice(
        0,
        availableSlots
      );

    setNewAttachments(
      (previous) => [
        ...previous,
        ...filesToAdd,
      ]
    );

    if (
      selectedFiles.length >
      availableSlots
    ) {
      invalidMessages.push(
        `You can upload a maximum of ${MAX_ATTACHMENTS} attachments.`
      );
    }

    if (invalidMessages.length) {
      swal.warning({
        icon: "warning",
        title:
          "Some files were not added",
        html: invalidMessages
          .map(
            (message) =>
              `<div style="text-align:left;margin-bottom:4px">${message}</div>`
          )
          .join(""),
      });
    }

    event.target.value = "";
  };

  const removeNewAttachment = (
    index
  ) => {
    setNewAttachments(
      (previous) =>
        previous.filter(
          (_, fileIndex) =>
            fileIndex !== index
        )
    );
  };

  const removeExistingAttachment = (
    attachment
  ) => {
    setExistingAttachments(
      (previous) =>
        previous.filter(
          (item) =>
            item !== attachment
        )
    );
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!form.customer) {
      validationErrors.customer =
        "Please select a customer.";
    }

    if (!form.requirementType) {
      validationErrors.requirementType =
        "Requirement type is required.";
    }

    if (!form.priority) {
      validationErrors.priority =
        "Priority is required.";
    }

    if (
      form.description &&
      form.description.length > 2000
    ) {
      validationErrors.description =
        "Description cannot exceed 2000 characters.";
    }

    if (
      isEdit &&
      !form.status
    ) {
      validationErrors.status =
        "Status is required.";
    }

    setErrors(validationErrors);

    return (
      Object.keys(
        validationErrors
      ).length === 0
    );
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const formData =
        new FormData();

      formData.append(
        "customer",
        form.customer
      );

      formData.append(
        "requirementType",
        form.requirementType
      );

      formData.append(
        "description",
        String(
          form.description || ""
        ).trim()
      );

      formData.append(
        "priority",
        form.priority
      );

      if (isEdit) {
        formData.append(
          "id",
          requirement._id
        );

        formData.append(
          "status",
          form.status
        );

        formData.append(
          "existingAttachments",
          JSON.stringify(
            existingAttachments
          )
        );
      } else {
        formData.append(
          "raisedBy",
          "staff"
        );
      }

      newAttachments.forEach(
        (file) => {
          formData.append(
            "attachments",
            file
          );
        }
      );

      const response = isEdit
        ? await api.postFormData(
            config.updateRequirementById,
            formData
          )
        : await api.postFormData(
            config.createCustomerRequirment,
            formData
          );

      if (
        response?.status ===
        "success"
      ) {
        await swal.success({
          icon: "success",
          title: isEdit
            ? "Requirement Updated"
            : "Requirement Created",
          text:
            response?.message ||
            `Requirement ${
              isEdit
                ? "updated"
                : "created"
            } successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });

        onSuccess(
          response?.data || null
        );
      } else {
        throw new Error(
          response?.message ||
            "Unable to save requirement."
        );
      }
    } catch (requestError) {
      console.error(
        "Save requirement error:",
        requestError
      );

      swal.error({
        icon: "error",
        title:
          "Unable to save requirement",
        text:
          requestError?.response?.data
            ?.message ||
          requestError?.message ||
          "Unable to save requirement.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const totalAttachmentCount =
    existingAttachments.length +
    newAttachments.length;

  return (
    <>
      <div className="requirement-form-overlay">
        <button
          type="button"
          className="requirement-form-backdrop"
          onClick={onClose}
          aria-label="Close form"
        />

        <div
          className="requirement-form-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={
            isEdit
              ? "Edit requirement"
              : "Add requirement"
          }
        >
          <div className="requirement-form-header">
            <div>
              <span>
                CUSTOMER REQUIREMENT
              </span>

              <h2>
                {isEdit
                  ? "Edit Requirement"
                  : "Add Requirement"}
              </h2>
            </div>

            <button
              type="button"
              className="requirement-form-close"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            >
              <FiX />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
          >
            <div className="requirement-form-body">
              <div className="requirement-form-grid">
                <div className="requirement-form-field">
                  <label>
                    Customer *
                  </label>

                  <CustomerSearchSelect
                    value={
                      form.customer
                    }
                    selectedCustomer={
                      selectedCustomer
                    }
                    onChange={
                      handleCustomerChange
                    }
                    onAddCustomer={
                      handleOpenAddCustomer
                    }
                    disabled={
                      submitting
                    }
                    error={
                      errors.customer
                    }
                  />
                </div>

                <div className="requirement-form-field">
                  <label>
                    Requirement Type *
                  </label>

                  <select
                    name="requirementType"
                    value={
                      form.requirementType
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      submitting
                    }
                  >
                    <option value="">
                      Select requirement
                    </option>

                    {REQUIREMENT_TYPES.map(
                      (type) => (
                        <option
                          key={type}
                          value={type}
                        >
                          {type}
                        </option>
                      )
                    )}
                  </select>

                  {errors.requirementType ? (
                    <small className="requirement-form-field-error">
                      {
                        errors.requirementType
                      }
                    </small>
                  ) : null}
                </div>

                <div className="requirement-form-field">
                  <label>
                    Priority *
                  </label>

                  <select
                    name="priority"
                    value={
                      form.priority
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      submitting
                    }
                  >
                    {REQUIREMENT_PRIORITIES.map(
                      (priority) => (
                        <option
                          key={priority}
                          value={priority}
                        >
                          {priority}
                        </option>
                      )
                    )}
                  </select>

                  {errors.priority ? (
                    <small className="requirement-form-field-error">
                      {errors.priority}
                    </small>
                  ) : null}
                </div>

                {isEdit ? (
                  <div className="requirement-form-field">
                    <label>
                      Status *
                    </label>

                    <select
                      name="status"
                      value={
                        form.status
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    >
                      {REQUIREMENT_STATUSES.map(
                        (status) => (
                          <option
                            key={status}
                            value={status}
                          >
                            {status}
                          </option>
                        )
                      )}
                    </select>

                    {errors.status ? (
                      <small className="requirement-form-field-error">
                        {errors.status}
                      </small>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="requirement-form-field">
                <label>
                  Description
                </label>

                <textarea
                  name="description"
                  rows={5}
                  value={
                    form.description
                  }
                  onChange={
                    handleChange
                  }
                  disabled={
                    submitting
                  }
                  placeholder="Describe the customer requirement..."
                />

                <div className="requirement-description-meta">
                  {errors.description ? (
                    <small className="requirement-form-field-error">
                      {
                        errors.description
                      }
                    </small>
                  ) : (
                    <span />
                  )}

                  <span>
                    {
                      form.description
                        .length
                    }
                    /2000
                  </span>
                </div>
              </div>

              <div className="requirement-upload-section">
                <div className="requirement-upload-heading">
                  <div>
                    <strong>
                      Attachments
                    </strong>

                    <span>
                      JPG, PNG, WebP or
                      PDF. Maximum 5 files,
                      5 MB each.
                    </span>
                  </div>

                  <label className="requirement-upload-button">
                    <FiUploadCloud />

                    Choose Files

                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={
                        handleFiles
                      }
                      disabled={
                        submitting ||
                        totalAttachmentCount >=
                          MAX_ATTACHMENTS
                      }
                    />
                  </label>
                </div>

                <div className="requirement-upload-count">
                  {totalAttachmentCount} /{" "}
                  {MAX_ATTACHMENTS} files
                </div>

                {totalAttachmentCount >
                0 ? (
                  <div className="requirement-file-list">
                    {existingAttachments.map(
                      (
                        attachment,
                        index
                      ) => (
                        <div
                          key={`${attachment}-${index}`}
                          className="requirement-file-item"
                        >
                          <FiFile />

                          <div>
                            <strong title={attachment}>
                              {getAttachmentFileName(
                                attachment
                              )}
                            </strong>

                            <span>
                              Existing attachment
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              removeExistingAttachment(
                                attachment
                              )
                            }
                            disabled={
                              submitting
                            }
                            aria-label="Remove attachment"
                          >
                            <FiX />
                          </button>
                        </div>
                      )
                    )}

                    {newAttachments.map(
                      (file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="requirement-file-item"
                        >
                          <FiFile />

                          <div>
                            <strong title={file.name}>
                              {file.name}
                            </strong>

                            <span>
                              {(
                                file.size /
                                1024 /
                                1024
                              ).toFixed(2)}{" "}
                              MB
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              removeNewAttachment(
                                index
                              )
                            }
                            disabled={
                              submitting
                            }
                            aria-label="Remove file"
                          >
                            <FiX />
                          </button>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className="requirement-no-files">
                    No attachments selected.
                  </div>
                )}
              </div>
            </div>

            <div className="requirement-form-footer">
              <button
                type="button"
                className="requirement-form-cancel"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="requirement-form-save"
                disabled={submitting}
              >
                <FiSave />

                {submitting
                  ? "Saving..."
                  : isEdit
                    ? "Update Requirement"
                    : "Create Requirement"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <AddCustomerDialog
        open={addCustomerOpen}
        initialValues={
          addCustomerInitialValues
        }
        onClose={() =>
          setAddCustomerOpen(false)
        }
        onSuccess={
          handleCustomerCreated
        }
      />
    </>
  );
};

export default AddEditRequirement;